/** Admin/Orgs.tsx — org (tenant) CRUD (SPEC §3 /orgs; super_admin). */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/provider';
import type { Org } from '@/types';

export default function Orgs() {
  const api = useApi();
  const qc = useQueryClient();
  const { data: orgs = [], isLoading, error } = useQuery({ queryKey: ['orgs'], queryFn: () => api.listOrgs() });

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');

  const create = useMutation({
    mutationFn: () => api.createOrg({ name, slug }),
    onSuccess: () => {
      setName('');
      setSlug('');
      void qc.invalidateQueries({ queryKey: ['orgs'] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteOrg(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['orgs'] }),
  });

  return (
    <div className="p-6 sm:p-8">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-helm-text">Organizations</h1>
          <p className="mt-0.5 text-sm text-helm-muted">Tenant organizations across the platform.</p>
        </div>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
        className="rh-card mb-6 flex flex-wrap items-end gap-4 p-5"
      >
        <Field label="Name" value={name} onChange={setName} required />
        <Field label="Slug" value={slug} onChange={setSlug} required placeholder="acme-corp" />
        <button type="submit" disabled={create.isPending} className="rh-btn-primary">
          Create org
        </button>
        {create.isError && <span className="text-sm text-helm-bad">Create failed.</span>}
      </form>

      <div className="rh-card overflow-hidden">
        <table className="rh-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Slug</th>
              <th>Parent</th>
              <th>Bootstrap</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {orgs.map((o: Org) => (
              <tr key={o.id}>
                <td className="font-medium text-helm-text">{o.name}</td>
                <td className="font-mono text-xs text-helm-muted">{o.slug}</td>
                <td className="font-mono text-xs text-helm-muted">{o.parent_org_id?.slice(0, 8) ?? '—'}</td>
                <td>
                  {o.bootstrap_done ? (
                    <span className="rh-badge bg-helm-good/15 text-helm-good">
                      <span className="h-1.5 w-1.5 rounded-full bg-helm-good" />
                      locked
                    </span>
                  ) : (
                    <span className="rh-badge bg-helm-warn/15 text-helm-warn">
                      <span className="h-1.5 w-1.5 rounded-full bg-helm-warn" />
                      open
                    </span>
                  )}
                </td>
                <td className="text-right">
                  <button
                    type="button"
                    onClick={() => remove.mutate(o.id)}
                    className="rh-btn-ghost px-2.5 py-1 text-xs text-helm-bad hover:bg-helm-bad/10"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {isLoading && (
              <tr>
                <td colSpan={5} className="py-12 text-center text-helm-muted">
                  Loading organizations…
                </td>
              </tr>
            )}
            {error && !isLoading && (
              <tr>
                <td colSpan={5} className="py-12 text-center text-helm-bad">
                  Failed to load organizations.
                </td>
              </tr>
            )}
            {orgs.length === 0 && !isLoading && !error && (
              <tr>
                <td colSpan={5} className="py-12 text-center text-helm-muted">
                  No organizations found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  required,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  placeholder?: string;
}) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-medium text-helm-muted">{label}</label>
      <input
        value={value}
        required={required}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="rh-input w-48"
      />
    </div>
  );
}
